/**
 * Per-kind analytic distance functions for the Three.js renderer.
 *
 * # Why this exists (Q13.C)
 *
 * The SVG renderer's hit-test (`apps/web/src/app/scene/hitTest.ts`) walks an
 * `EntityPreview` property bag and computes geometric distance per type.
 * The Three.js renderer can't reuse it directly — its input is a
 * `RenderableEntity` whose geometry is already parsed into typed
 * `SceneGeometry` payloads in offset-world coordinates.
 *
 * Per the plan PR-C decision (Q13.C):
 *   * Math is **ported, not reused** — the algorithms are identical to
 *     hitTest.ts:47–183, with object accessors swapped for the typed
 *     SceneGeometry shape.
 *   * Equivalence is enforced by `equivalence.test.ts`: ≥ 99.5 % match
 *     across 200 fixture cases × 3 tolerances.
 *
 * # Coordinate space contract
 *
 * The `worldPoint` parameter MUST already be in offset-world coords (i.e.
 * `clientToWorld` has been called and the coordinate origin was applied
 * by `applyOffset`). RenderableEntity.bounds and the `Vec3` payloads
 * inside `geometry` are also in offset-world. Both inputs share the same
 * frame so distances come out in offset-world units, which equals
 * AutoCAD-world units (the offset is a pure translation).
 */

import {
  pointInPolygon,
  distanceToSegment2D,
  isDefaultExtrusion,
  isPointInFilledRegion,
  renderedBoundaryLoopIndices,
} from "@dxf-ai-editor/drawing-model";

import { tessellateEllipse } from "../adapters/arcEllipseSpline";
import { polylineCentrelineSamples } from "../adapters/geom/polylineWidthBand";
import {
  drawnBandFilledRegion,
  fillModePaintsSolid,
} from "../adapters/geom/polylineDrawnBand";
import {
  drawnPolylineSweptRuns,
  type DrawnPolylineShape,
} from "../adapters/geom/polylineThicknessSweep";
import { pointInPaintedHatch } from "./hatchFilledRegion";
import type { ChildGeometryFilter } from "../layerChainVisibility";
import type {
  RenderableEntity,
  SceneGeometry,
  Vec2,
  Vec3,
} from "../types";

/** Axis-aligned bbox in offset-world coords. */
export interface BBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Distance from a point to a finite line segment in 2D. XY plane only —
 * z is ignored so this works for both Vec2 and Vec3 inputs.
 *
 * Ported from hitTest.ts:47.
 */
export function distanceToLineSegment(p: Vec2, a: Vec2, b: Vec2): number {
  // Delegates to the drawing-model one-truth `distanceToSegment2D`; keeps the
  // historical name + Vec2 signature for back-compat (same as isPointInPolygon).
  return distanceToSegment2D(p, a, b);
}

/**
 * Distance from a point to a circle's stroked rim (i.e. the absolute
 * radial offset). Returns 0 exactly on the circle.
 *
 * Ported from hitTest.ts:62.
 */
export function distanceToCircle(
  p: Vec2,
  center: Vec2,
  radius: number,
): number {
  const d = Math.hypot(p.x - center.x, p.y - center.y);
  return Math.abs(d - radius);
}

/**
 * Distance from a point to an arc's stroked rim. If the point's polar
 * angle (from the arc center) falls inside the arc sweep, the distance
 * is the radial offset; otherwise we fall back to the closer endpoint.
 *
 * Ported from hitTest.ts:71.
 */
export function distanceToArc(
  p: Vec2,
  center: Vec2,
  radius: number,
  startRad: number,
  endRad: number,
): number {
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  const pointAngle = Math.atan2(dy, dx);
  const sweep = normalizeSweep(startRad, endRad);
  const normalizedStart = normalizeAngle(startRad);
  const angleFromStart = normalizeAngle(pointAngle - normalizedStart);

  if (angleFromStart <= sweep) {
    return Math.abs(Math.hypot(dx, dy) - radius);
  }

  const endpoint1 = {
    x: center.x + radius * Math.cos(startRad),
    y: center.y + radius * Math.sin(startRad),
  };
  const endpoint2 = {
    x: center.x + radius * Math.cos(endRad),
    y: center.y + radius * Math.sin(endRad),
  };
  return Math.min(
    Math.hypot(p.x - endpoint1.x, p.y - endpoint1.y),
    Math.hypot(p.x - endpoint2.x, p.y - endpoint2.y),
  );
}

function normalizeAngle(angle: number): number {
  const twoPi = Math.PI * 2;
  return ((angle % twoPi) + twoPi) % twoPi;
}

function normalizeSweep(startRad: number, endRad: number): number {
  const twoPi = Math.PI * 2;
  const rawSweep = endRad - startRad;
  const normalized = ((rawSweep % twoPi) + twoPi) % twoPi;
  return normalized === 0 && startRad !== endRad ? twoPi : normalized;
}

/**
 * Distance from a point to a polyline. `bulges[i]` (when non-zero)
 * encodes a circular arc on the segment from vertices[i] →
 * vertices[i+1]; we expand it via `distanceToBulgeArc`.
 *
 * Ported from hitTest.ts:117.
 */
export function distanceToPolyline(
  p: Vec2,
  vertices: readonly Vec2[],
  closed: boolean,
  bulges?: readonly number[],
): number {
  if (vertices.length === 0) return Number.POSITIVE_INFINITY;
  if (vertices.length === 1) {
    return Math.hypot(p.x - vertices[0]!.x, p.y - vertices[0]!.y);
  }

  const segmentCount = closed ? vertices.length : vertices.length - 1;
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < segmentCount; i += 1) {
    const a = vertices[i]!;
    const b = vertices[(i + 1) % vertices.length]!;
    const bulge = bulges?.[i] ?? 0;
    const d =
      bulge !== 0
        ? distanceToBulgeArc(p, a, b, bulge)
        : distanceToLineSegment(p, a, b);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Distance to a polyline segment with a non-zero bulge. The DXF bulge
 * value encodes the tangent of 1/4 of the included angle (positive =
 * CCW, negative = CW). Converts to a circular arc with computed centre
 * + radius and reuses `distanceToArc`.
 *
 * The arc it resolves is the SAME one `drawing-model/bulgeArc.ts` resolves —
 * pinned by `distance.bulgeOrientation.property.test.ts`, which compares this
 * function's implied curve against that one-truth primitive over generated
 * (chord, bulge) pairs. It is re-derived here rather than imported because
 * `distanceToArc`'s CCW-angle-pair contract is what this file needs, not a
 * signed sweep; the property test is what keeps the two from drifting again
 * (they DID drift — see the orientation note inside).
 *
 * Ported from hitTest.ts:149.
 */
export function distanceToBulgeArc(
  p: Vec2,
  a: Vec2,
  b: Vec2,
  bulge: number,
): number {
  const chordX = b.x - a.x;
  const chordY = b.y - a.y;
  const chordLen = Math.hypot(chordX, chordY);
  if (chordLen === 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  const sagitta = (chordLen / 2) * bulge;
  const radius = (chordLen * (1 + bulge * bulge)) / (4 * Math.abs(bulge));

  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  const perpX = -chordY / chordLen;
  const perpY = chordX / chordLen;
  const h = radius - Math.abs(sagitta);
  const sign = bulge > 0 ? 1 : -1;
  // ORIENTATION (r17 P1). This offset used to be NEGATED (`midX - sign*...`),
  // an independent second copy of the same mirror `drawing-model/bulgeArc` was
  // carrying: it put the centre on the wrong side of the chord, so the pick
  // measured a "phantom bow" the canvas never drew and a hit test could answer
  // in empty space. Per the Autodesk DXF reference the group-42 bulge is
  // negative iff the arc runs CLOCKWISE start→end, so a POSITIVE bulge sweeps
  // CCW and its centre sits to the LEFT of the chord direction — `perp` here is
  // exactly that +90° normal. Checked against ezdxf 1.4.4: `bulge_to_arc(
  // (0,0), (20,0), 0.5)` → centre (10, +7.5), which is what this now returns.
  const centerX = midX + sign * perpX * h;
  const centerY = midY + sign * perpY * h;

  const startAngle = Math.atan2(a.y - centerY, a.x - centerX);
  const endAngle = Math.atan2(b.y - centerY, b.x - centerX);

  return bulge > 0
    ? distanceToArc(p, { x: centerX, y: centerY }, radius, startAngle, endAngle)
    : distanceToArc(
        p,
        { x: centerX, y: centerY },
        radius,
        endAngle,
        startAngle,
      );
}

/**
 * Distance from a point to an axis-aligned rectangle. Returns 0 if the
 * point lies inside (or on the boundary of) the rectangle.
 *
 * Ported from hitTest.ts:192.
 */
export function distanceToBBox(p: Vec2, bbox: BBox): number {
  const dx = Math.max(bbox.left - p.x, 0, p.x - bbox.right);
  const dy = Math.max(bbox.top - p.y, 0, p.y - bbox.bottom);
  return Math.hypot(dx, dy);
}

/**
 * Standard ray-cast point-in-polygon. Delegates to the shared `pointGeometry`
 * one-truth in `drawing-model` (the SVG hit-test, this renderer pick and the
 * HATCH pick all share it — was 3 copies, jscpd-flagged). Re-exported under the
 * historical `isPointInPolygon` name + `Vec2` signature for back-compat.
 */
export function isPointInPolygon(p: Vec2, polygon: Vec2[]): boolean {
  return pointInPolygon(p, polygon);
}

/**
 * Approximate an ellipse arc as a series of polyline samples and pick the
 * closest segment. The ellipse has center, majorAxis (FULL 3D vector),
 * ratio = minor/major, parametric range [start, end], and the optional OCS
 * `extrusion` normal (DXF 210/220/230, absent ⇒ (0,0,1)).
 *
 * SL-15 round-8 @codex F4 — reuse the ONE-TRUTH 3D sampler the RENDERER uses
 * (`tessellateEllipse`, the same call `batchedLayer.geometryToLineVertices`
 * makes) so a TILTED ellipse (out-of-plane major axis and/or a non-default OCS
 * normal) is hit-testable exactly where it renders. The pick compares in XY
 * (top-down view), so we take the visible XY silhouette of the 3D samples
 * (`distanceToLineSegment` ignores z). A PLANAR ellipse yields byte-identical
 * samples to the prior XY-only math (tessellateEllipse doc), so the
 * pick-equivalence baseline is unchanged; before this fix the flat sampler
 * missed a tilted ellipse's true rim.
 */
function distanceToEllipseSamples(
  p: Vec2,
  center: Vec3,
  majorAxis: Vec3,
  ratio: number,
  startParam: number,
  endParam: number,
  extrusion?: Vec3,
): number {
  // 64 samples — same baseline tessellation as batchedLayer for render parity.
  const samples = tessellateEllipse(
    center,
    majorAxis,
    ratio,
    startParam,
    endParam,
    64,
    extrusion,
  );
  if (samples.length < 2) return Number.POSITIVE_INFINITY;
  let minDist = Number.POSITIVE_INFINITY;
  for (let i = 1; i < samples.length; i++) {
    const d = distanceToLineSegment(p, samples[i - 1]!, samples[i]!);
    if (d < minDist) minDist = d;
  }
  return minDist;
}

/**
 * Approximate a SPLINE as polyline through control points and pick the
 * closest segment. Matches the coarse rendering tessellation in
 * `batchedLayer.ts:geometryToLineVertices` (line 350-353), which uses
 * control-point polyline as the PR-B baseline for splines. A future PR
 * upgrades both render and pick to a real de-Boor sampler in lock-step.
 */
function distanceToSplineControlPolyline(
  p: Vec2,
  controlPoints: Vec3[],
): number {
  if (controlPoints.length === 0) return Number.POSITIVE_INFINITY;
  if (controlPoints.length === 1) {
    return Math.hypot(p.x - controlPoints[0]!.x, p.y - controlPoints[0]!.y);
  }
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < controlPoints.length - 1; i++) {
    const a = controlPoints[i]!;
    const b = controlPoints[i + 1]!;
    const d = distanceToLineSegment(p, a, b);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Convert RenderableEntity.bounds to BBox shape (different field names).
 */
function boundsToBBox(bounds: RenderableEntity["bounds"]): BBox {
  return {
    left: bounds.minX,
    right: bounds.maxX,
    top: bounds.minY,
    bottom: bounds.maxY,
  };
}

/**
 * Pointer distance to what a polyline is actually DRAWN as — the BAND a
 * width-carrying run is stroked as, the group-39 SWEPT surface an extruded one
 * generates, or both.
 *
 * # The defect this closes (@codex PR #785 round 11, F3 — and round 16)
 *
 * ROUND 11. The `"polyline"` arm measured the CENTRELINE for every polyline,
 * width or not — a curve the batcher does not stroke for a wide one
 * (`batchedLayer.ts::widePolylineLineVertices` emits the band instead, and the
 * fill mesh paints it). For a ribbon wider than the pick aperture that inverts
 * the pick: clicking directly on a VISIBLE band edge reported roughly half the
 * width and missed, while clicking the INVISIBLE centreline reported 0 and
 * selected. Window / crossing select had already been converted to the same
 * band in round 7 (`windowSelect.ts`), so the two selection paths disagreed
 * about the same entity.
 *
 * ROUND 16, THE SAME DEFECT ONE LAYER OUT. Round 15 (`8c56d08`) gave the canvas
 * the DXF group-39 thickness sweep — the base outline, its copy translated by
 * `t·N̂`, and the rulings between them — and this arm went on measuring the
 * unswept base. With an oblique group-210 normal the displacement has real XY
 * extent, so a user could SEE the painted top outline, click exactly on it, and
 * select nothing: extrusion `(1,1,1)`, thickness 5, constant width 4 on
 * `(0,0) → (10,0)` paints a top vertex at `(3.7032479268758554,
 * 3.7032479268758554)` which this function reported 4.08248290463863 away
 * against a 4-CSS-px aperture worth 0.08 world units, while the entity's own
 * `bounds` (`maxX` 3.7032479268758554) had admitted the click all along. Making
 * geometry visible without making it pickable is a regression introduced by that
 * fix, not an inherited limitation, so the runs come from
 * {@link drawnPolylineSweptRuns} — the SAME assembly
 * `batchedLayer.ts::geometryToLineVertices` flattens into the paint stream.
 *
 * # What "on the entity" means, and why FILLMODE decides it
 *
 * At `$FILLMODE` 1 — the Autodesk documented INITIAL VALUE, and what an absent
 * `fillmode` means — the band is a SOLID region, so anywhere INSIDE the ribbon
 * is on the entity and the distance is 0. At `$FILLMODE` 0 the canvas draws
 * only the silhouette, so the hollow interior (the centreline included) must
 * MISS and the distance is the one to the nearest drawn edge. The renderer and
 * this pick read the same {@link fillModePaintsSolid}, so what the user can
 * click is exactly what the user can see.
 *
 * THE FILL TEST USES THE BASE LOOPS ONLY — `DrawnPolylineShape.bandLoops`, never
 * the swept copy. `scene/widePolylineFillMesh.ts` triangulates the base band and
 * strokes the top face as an OUTLINE (residual 2 in
 * `adapters/geom/polylineThicknessSweep.ts`), so treating the top ribbon's
 * interior as solid here would make the pick a SUPERSET of the paint — the
 * original bug in the other direction.
 *
 * The inside test runs in WORLD XY on the WORLD loops, because the pointer is a
 * top-view XY position and the shape the user is aiming at is the band's XY
 * projection. Only the exterior/hole ROLES are decided in the band's own plane,
 * where a tilted band is not squashed; applying those roles to the world loops
 * then lets a tilted band's degenerate shadow fall through to the edge
 * measurement, which is what a top view of an edge-on ribbon should do. Both
 * halves are {@link drawnBandFilledRegion} — the SAME region CROSSING SELECT
 * builds (`windowSelect.ts`), so the two selection paths cannot drift apart
 * about the same painted ribbon again (@codex PR #785 round 12, F1).
 *
 * `isPointInFilledRegion` is `drawing-model`'s one-truth exterior-minus-holes
 * predicate — the SAME call the HATCH arm below makes through
 * {@link pointInPaintedHatch}, so a closed band's
 * annulus HOLE is empty for the pick exactly as it is empty on screen.
 */
function distanceToDrawnPolyline(
  worldPoint: Vec2,
  geom: Extract<SceneGeometry, { kind: "polyline" }>,
  drawn: DrawnPolylineShape,
  fillmode: number | undefined,
): number {
  if (
    drawn.bandLoops &&
    fillModePaintsSolid(fillmode) &&
    isPointInFilledRegion(
      worldPoint,
      drawnBandFilledRegion(drawn.bandLoops, geom),
    )
  ) {
    return 0;
  }
  // Outside the painted region (or an explicit FILLMODE 0) → the nearest drawn
  // edge. A band loop is a closed ring by construction, so its closing edge
  // counts; a swept-top copy carries its base's own topology and a RULING is a
  // 2-point open run. None of them carries bulges or widths, which is what keeps
  // this from recursing.
  let min = Number.POSITIVE_INFINITY;
  for (const run of drawn.runs) {
    const d = distanceToPolyline(worldPoint, run.points, run.closed);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Top-level distance dispatcher for a RenderableEntity. Returns null
 * when the entity type / kind has no analytic distance function (caller
 * must skip it during the pick).
 *
 * Mirrors hitTest.ts:260 (`distanceToEntity`). Per-kind branches:
 *
 *   line, circle, arc, polyline → analytic (1:1 with SVG path). A polyline
 *                                 carrying DXF WIDTH is measured against the
 *                                 BAND it is drawn as, filled or outlined per
 *                                 the drawing's `$FILLMODE`, and one carrying a
 *                                 group-39 THICKNESS against the swept surface
 *                                 the canvas paints — see
 *                                 `distanceToDrawnPolyline`.
 *   ellipse, spline             → polyline approximation that matches
 *                                 the renderer's PR-B tessellation. SVG
 *                                 hit-test currently falls back to
 *                                 bbox for these, which the equivalence
 *                                 test treats as acceptable drift.
 *   hatch                       → point-in-polygon on outermost loop;
 *                                 falls back to bbox if the loop is
 *                                 degenerate.
 *   mesh-wire                   → min over every wire run (a heavy POLYLINE
 *                                 mesh's rows/columns or visible face edges),
 *                                 never the AABB;
 *   block-insert                → min over the currently VISIBLE
 *                                 `childRenderables` (cache-direct wrapper,
 *                                 Codex #784 P2 + round 3 P2 `childFilter`);
 *                                 bbox for the legacy instanced stub (no
 *                                 children).
 *   text/mtext/dimension/multileader/viewport/wipeout/array-rect
 *                               → bbox (matches SVG hit-test).
 */
export function distanceToRenderable(
  worldPoint: Vec2,
  entity: RenderableEntity,
  /**
   * Codex PR #784 round 3 P2 — optional "is this child currently DRAWN"
   * predicate applied to a `block-insert` wrapper's `childRenderables`
   * (`layerChainVisibility.ts makeVisibleChildGeometryFilter`). Omitted /
   * `null` → every child participates (nothing is hidden).
   */
  childFilter?: ChildGeometryFilter | null,
): number | null {
  const geom = entity.geometry;
  switch (geom.kind) {
    case "line": {
      return distanceToLineSegment(worldPoint, geom.a, geom.b);
    }
    case "circle": {
      return distanceToCircle(worldPoint, geom.center, geom.radius);
    }
    case "arc": {
      return distanceToArc(
        worldPoint,
        geom.center,
        geom.radius,
        geom.start,
        geom.end,
      );
    }
    case "polyline": {
      // A polyline carrying authored WIDTHS is drawn as its BAND, and one
      // carrying a group-39 THICKNESS is drawn as the swept surface that band
      // (or that centreline) generates — in neither case is the bare centreline
      // the thing on screen. `drawnPolylineSweptRuns` is the SAME assembly the
      // batcher paints from, and it answers `null` for the width-less,
      // thickness-less majority after one field read each, so the centreline
      // path below stays byte-identical.
      const drawn = drawnPolylineSweptRuns(geom);
      if (drawn) {
        return distanceToDrawnPolyline(
          worldPoint,
          geom,
          drawn,
          entity.fillmode,
        );
      }
      // A BULGED span of an OCS-planar entity in a TILTED plane is an arc in
      // THAT plane, and `batchedLayer.ts` draws it there
      // (`adapters/geom/polylineWidthBand.ts::polylineCentrelineSamples`).
      // Measuring against `bulgeArc` on the lifted points would put the pick on
      // a curve that is not on screen — for extrusion (0,1,0) a semicircle's
      // bow is drawn on world Z while the flat circle bows along world Y — so a
      // click ON the drawn arc would miss and a click on empty space would hit.
      // The samples ARE the drawn chain, so the pick is measured against
      // exactly what the batcher packed; `polylineCentrelineSamples` returns
      // the input array untouched for every default-extrusion / bulge-free
      // polyline, which then takes the byte-identical path it always did.
      if (isDefaultExtrusion(geom.extrusion)) {
        return distanceToPolyline(
          worldPoint,
          geom.points,
          geom.closed,
          geom.bulges,
        );
      }
      return distanceToPolyline(
        worldPoint,
        polylineCentrelineSamples(
          geom.points,
          geom.closed,
          geom.bulges,
          geom.extrusion,
        ),
        geom.closed,
      );
    }
    case "mesh-wire": {
      // A heavy POLYLINE mesh is picked against the wire runs it DRAWS (rows
      // and columns, or visible face edges) — never its AABB: a cursor in the
      // empty middle of a coarse mesh cell must miss, exactly as it does for a
      // sparse `block-insert`'s children below. Runs are bulge-free by
      // construction (group 42 is a 2D-polyline field).
      let min = Number.POSITIVE_INFINITY;
      for (const run of geom.runs) {
        const d = distanceToPolyline(worldPoint, run.points, run.closed);
        if (d < min) min = d;
      }
      return Number.isFinite(min) ? min : null;
    }
    case "ellipse": {
      return distanceToEllipseSamples(
        worldPoint,
        geom.center,
        geom.majorAxis,
        geom.ratio,
        geom.start,
        geom.end,
        geom.extrusion,
      );
    }
    case "spline": {
      return distanceToSplineControlPolyline(worldPoint, geom.controlPoints);
    }
    case "hatch": {
      // A click is INSIDE the hatch iff it lands on the PAINTED area — the
      // drawing's `$FILLMODE` fills the hatch at all, AND the point is inside
      // some exterior loop AND NOT inside any HOLE of that island. The renderer
      // (`tessellateHatchLoops`) subtracts each island's holes, so a click in an
      // EMPTY hole (no fill drawn there) must NOT select the hatch, and at an
      // explicit `$FILLMODE` 0 `scene/sceneMeshFactories.ts::buildHatchFillMesh`
      // emits NO fill of any tier, so NOTHING inside is drawn and the pick must
      // fall through to the boundary distance below (@codex PR #785 round 17,
      // P2 — the hatch half of the FILLMODE reading the WIDE POLYLINE band has
      // had since rounds 11/12; `distanceToDrawnPolyline` above is the same
      // sentence for the band). Both the render and this pick go through
      // {@link pointInPaintedHatch}, which wraps the one-truth
      // `isPointInFilledRegion` and the one `fillModePaintsSolid` reader, so
      // they agree cell-for-cell on the painted region (incl. the multi-island,
      // the no-exterior fallback and the tagged-degenerate case). CROSSING
      // SELECT calls the SAME function (`windowSelect.ts`). Mirrors
      // drawing-model `distanceToHatchBoundary`.
      const usable = geom.loops.filter((l) => l.vertices.length >= 3);
      if (usable.length === 0) {
        return distanceToBBox(worldPoint, boundsToBBox(entity.bounds));
      }
      if (pointInPaintedHatch(worldPoint, entity)) {
        return 0;
      }
      // Outside the painted region (inside an empty hole, or ANYWHERE at an
      // explicit `$FILLMODE` 0, where `batchedLayer.ts::hatchOutlineLineVertices`
      // strokes the boundary loops and the interior is empty screen) → nearest
      // edge distance, measured ONLY over the loops the renderer actually emits
      // (every exterior + each OWNED hole). An UNOWNED orphan hole is DROPPED by
      // `tessellateHatchLoops` and is invisible, so a click near it must NOT
      // register as near the hatch (PR #410 P3). `renderedBoundaryLoopIndices` is
      // the SHARED one-truth for "which loops the renderer draws" (same fn the
      // HATCHEDIT pick uses), keeping per-loop bulges for a bulge-accurate
      // outside distance.
      //
      // `geom.hadTaggedExterior` (the RAW-source `pathTypeFlag` bit, computed
      // pre-drop in `buildHatchSceneGeometry`) threads the tagged-degenerate signal:
      // a hatch that tagged an exterior whose loop degenerated away renders NOTHING,
      // so the rendered-loop set is EMPTY and the nearest distance stays +Infinity —
      // a click near the leftover interior loop must NOT register as near the hatch
      // (PR #410 P3), matching the empty render + the `isPointInFilledRegion` empty
      // above. Re-deriving it from `usable`/`geom.loops` here would read FALSE (the
      // degenerate exterior was dropped by the walk) and wrongly promote the interior.
      const rendered = renderedBoundaryLoopIndices(
        usable.map((l) => ({ vertices: l.vertices, isHole: l.kind === "interior" })),
        geom.hadTaggedExterior,
      );
      let nearest = Number.POSITIVE_INFINITY;
      for (const i of rendered) {
        const loop = usable[i]!;
        const d = distanceToPolyline(worldPoint, loop.vertices, true, loop.bulges);
        if (d < nearest) nearest = d;
      }
      return nearest;
    }
    case "point": {
      return Math.hypot(
        worldPoint.x - geom.position.x,
        worldPoint.y - geom.position.y,
      );
    }
    case "block-insert": {
      // Codex PR #784 P2 — on the cache-direct path the INSERT wrapper is the
      // ONLY pickable object and its `childRenderables` are the drawn strokes
      // (`scene/liftBlockInsert.ts`). Measure against the children so a pick
      // follows the visible geometry (an empty region of a sparse block is a
      // miss, a child stroke is a hit — same as the legacy branch, where each
      // child is its own spatial-index entry). The wrapper's bounds stay the
      // rule for the legacy InstancedMesh stub (empty `childRenderables`).
      return distanceToBlockInsertChildren(
        worldPoint,
        geom.childRenderables,
        entity.bounds,
        childFilter ?? null,
      );
    }
    case "text":
    case "mtext":
    case "dimension":
    case "multileader":
    case "viewport":
    case "wipeout":
    case "array-rect": {
      return distanceToBBox(worldPoint, boundsToBBox(entity.bounds));
    }
    default: {
      // Exhaustiveness guard — if a new SceneGeometry kind is added and
      // we forget to handle it here, return null so the pick skips it
      // (matches hitTest.ts default branch).
      const _exhaustive: never = geom;
      void _exhaustive;
      return null;
    }
  }
}

/**
 * Minimum analytic distance over an INSERT wrapper's render-only children
 * (recursing through {@link distanceToRenderable}, so a nested `block-insert`
 * child or any per-kind child measures with its own arm). The children are
 * NEVER exposed as pick results — the caller keeps reporting the WRAPPER's id;
 * this only decides how far the cursor is from the reference's drawn strokes.
 *
 * Falls back to the wrapper's bounds box when there are no children (the
 * legacy InstancedMesh stub) or when no child yields a measurable distance
 * (every child of an unsupported kind — nothing drawn to follow). Children
 * `childFilter` rejects are not drawn at all (round 3 P2): they are skipped,
 * and when they were the ONLY measurable children the reference draws nothing,
 * so the result is `+Infinity` (a guaranteed miss) rather than the bbox.
 */
function distanceToBlockInsertChildren(
  worldPoint: Vec2,
  children: ReadonlyArray<RenderableEntity>,
  wrapperBounds: RenderableEntity["bounds"],
  childFilter: ChildGeometryFilter | null,
): number | null {
  let best: number | null = null;
  let hiddenChildren = 0;
  for (const child of children) {
    // Codex PR #784 round 3 P2 — a child hidden by its OWN layer chain (global
    // off / freeze, or frozen in the active viewport) draws nothing, so a click
    // where it WOULD have been must not hit the INSERT.
    if (childFilter !== null && !childFilter(child)) {
      hiddenChildren++;
      continue;
    }
    const d = distanceToRenderable(worldPoint, child, childFilter);
    if (d === null) continue;
    if (best === null || d < best) best = d;
  }
  if (best !== null) return best;
  // Every measurable child was hidden — the reference draws nothing here, so
  // the pick must miss everywhere (the same "renders nothing ⇒ +Infinity" rule
  // the fully-degenerate HATCH arm uses) instead of falling back to the bbox.
  if (hiddenChildren > 0) return Number.POSITIVE_INFINITY;
  return distanceToBBox(worldPoint, boundsToBBox(wrapperBounds));
}

/**
 * AABB rejection helper — same shape as hitTest.ts:`pointOutsideExpandedBounds`.
 * Used by `pick.ts` to avoid evaluating expensive analytic distance for
 * candidates whose bbox is far from the query point.
 */
export function pointOutsideExpandedBounds(
  p: Vec2,
  bounds: RenderableEntity["bounds"],
  tolerance: number,
): boolean {
  return (
    p.x < bounds.minX - tolerance ||
    p.x > bounds.maxX + tolerance ||
    p.y < bounds.minY - tolerance ||
    p.y > bounds.maxY + tolerance
  );
}

/** Re-export for tests / pick.ts callers that don't want to compute it. */
export type { SceneGeometry };
